import type { ReactNode } from 'react';
import type { TenantFeature } from '@kawai/shared';
import { Button, Card } from '@kawai/ui';
import { useAuth } from '../lib/auth.js';

export interface RequireFeatureProps {
  /** 必須となる機能パッケージ */
  feature: TenantFeature;
  children: ReactNode;
  /**
   * fallback 描画。未指定なら標準の「契約していません」表示を出す。
   * tenant-portal への redirect は呼出側で `<Navigate>` を渡して構成する。
   */
  fallback?: ReactNode;
}

/**
 * Phase 5 (T-06): テナントが指定 feature を契約しているか検証するガード。
 *
 * 判定ルール:
 * - status='loading'   : 「読み込み中…」を出す（チラつき防止）
 * - status='signed-out': null を返す（呼出元の AuthGuard が /signin へ送る前提）
 * - role==='saas-admin': features を見ずに素通り（テナント横断アクセス）
 * - features.includes(feature) : 子を描画
 * - それ以外            : fallback or 標準「契約していません」表示
 *
 * 通常は `<AuthGuard>` の内側で使う：
 *   <AuthGuard><RequireFeature feature='fuel'>...</RequireFeature></AuthGuard>
 */
export function RequireFeature({ feature, children, fallback }: RequireFeatureProps) {
  const { status, role, features, signOut } = useAuth();

  if (status === 'loading') {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-full items-center justify-center text-sm text-slate-500"
      >
        読み込み中…
      </div>
    );
  }

  if (status === 'signed-out') {
    return null;
  }

  if (role === 'saas-admin') {
    return <>{children}</>;
  }

  if (features.includes(feature)) {
    return <>{children}</>;
  }

  if (fallback !== undefined) {
    return <>{fallback}</>;
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <Card padding="none" className="w-full max-w-sm p-6 text-center">
        <h1 className="text-base font-bold text-slate-900">この機能は契約に含まれていません</h1>
        <p className="mt-2 text-sm text-slate-600">
          {`「${feature}」 はお使いのテナントで利用できません。`}
          <br />
          ご契約内容の追加が必要な場合は管理者へお問い合わせください。
        </p>
        <Button
          variant="dark"
          fullWidth
          onClick={() => {
            void signOut();
          }}
          className="mt-4"
        >
          サインアウト
        </Button>
      </Card>
    </div>
  );
}
